import React, { useState } from 'react';
import T from '../styles/theme';
import {Badge, Btn, Select , Checkbox} from '../components/UI';
import { useStore } from '../data/store';

/* Evaluate a calculation formula against an item */
function evalCalc(formula, item) {
  try {
    let f = formula || "";
    for (const key of Object.keys(item)) {
      if (typeof item[key] === "number") f = f.replace(new RegExp("\\b" + key + "\\b", "g"), item[key]);
    }
    const safe = f.replace(/[^0-9+\-*/().,%\s]/g, "");
    const result = Function('"use strict"; return (' + safe + ')')();
    return isNaN(result) ? 0 : result;
  } catch(e) { return 0; }
}

/* Render a field input */
function FieldInput({ field, value, onChange, compact }) {
  const s = { width: "100%", background: T.sf, backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", backdropFilter: "blur(16px) saturate(1.4)", WebkitBackdropFilter: "blur(16px) saturate(1.4)", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 4, padding: compact ? "4px 6px" : "6px 8px", color: T.txt, fontSize: compact ? 10 : 11, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  switch (field.type) {
    case "textarea":
      return <textarea value={value || ""} onChange={e => onChange(e.target.value)} placeholder={field.label + "..."} style={{ ...s, resize: "none", height: compact ? 36 : 50 }} />;
    case "select":
      return (
        <Select value={value || ""} onChange={e => onChange(e.target.value)} style={{ ...s, cursor: "pointer" }}>
          <option value="">Select {field.label}...</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    case "multiselect":
      return (
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {(field.options || []).map(o => (
            <button key={o} onClick={() => {
              const cur = value || [];
              onChange(cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o]);
            }} style={{
              padding: "2px 6px", borderRadius: 3, fontSize: 9, cursor: "pointer",
              border: (value || []).includes(o) ? "2px solid " + T.blue : "1px solid " + T.bdr,
              background: (value || []).includes(o) ? T.blue + "12" : T.sf,
              color: (value || []).includes(o) ? T.blue : T.tx3, fontWeight: 400,
            }}>{o}</button>
          ))}
        </div>
      );
    case "checkbox":
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.tx2, cursor: "pointer" }}>
          <Checkbox checked={!!value} onChange={e => onChange(e.target.checked)} />
          {field.label}
        </label>
      );
    case "number": case "currency":
      return <input type="number" value={value || ""} onChange={e => onChange(e.target.value ? Number(e.target.value) : "")} placeholder={field.type === "currency" ? "$0" : "0"} style={s} />;
    case "date":
      return <input type="date" value={value || ""} onChange={e => onChange(e.target.value)} style={s} />;
    case "calculation":
      return null; // Calculation fields are read-only, rendered separately
    default:
      return <input type={field.type === "email" ? "email" : field.type === "url" ? "url" : "text"} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={field.label + "..."} style={s} />;
  }
}

/* Format a field value for display */
function formatFieldValue(field, value, item) {
  if (field.type === "calculation") {
    const v = evalCalc(field.formula, item);
    if (field.calcFormat === "currency") return "$" + Number(v).toLocaleString();
    if (field.calcFormat === "percent") return Number(v).toFixed(1) + "%";
    return Number(v).toLocaleString();
  }
  if (field.type === "currency" && value) return "$" + Number(value).toLocaleString();
  if (field.type === "checkbox") return value ? "Yes" : "No";
  if (field.type === "multiselect" && Array.isArray(value)) return value.join(", ");
  return value || "—";
}

export default function CustomPipelinePage({ pipelineId }) {
  const store = useStore();
  const pipe = store.customPipelines.find(p => p.id === pipelineId);
  const items = store.customPipelineItems.filter(i => i.pipelineId === pipelineId);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showAdd, setShowAdd] = useState(null);
  const [newItemData, setNewItemData] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  if (!pipe) return <div style={{ padding: 40, textAlign: "center", color: T.tx3 }}>Pipeline not found. Create one in Settings.</div>;

  const stages = pipe.stages || [];
  const fields = pipe.fields || [];
  const priField = fields.find(f => f.type === "select" && f.label.toLowerCase().includes("priority"));
  const priColors = { Low: T.tx3, Medium: T.amb, High: T.red, Urgent: "#ff3366" };

  // Figure out which fields to show on card vs detail
  const titleField = fields.find(f => f.id === "title") || fields[0];
  const descField = fields.find(f => f.id === "description" || f.type === "textarea");
  const assigneeField = fields.find(f => f.id === "assignee" || f.label.toLowerCase().includes("assignee") || f.label.toLowerCase().includes("owner"));
  const cardFields = fields.filter(f => f !== titleField && f !== descField); // all non-title/desc on card

  const addItem = (stageName) => {
    const titleVal = newItemData[titleField?.id] || newItemData.title;
    if (!titleVal) return;
    store.addPipelineItem({ pipelineId, stage: stageName, ...newItemData });
    setNewItemData({}); setShowAdd(null);
  };

  const updateItemField = (itemId, fieldId, value) => {
    store.updatePipelineItem(itemId, { [fieldId]: value });
  };

  return (
    <div style={{ display: "flex", height: "100%", background: "transparent" }}>
      {/* Kanban */}
      <div style={{ flex: 1, display: "flex", gap: 8, overflowX: "auto", padding: 8, height: "100%" }}>
        {stages.map(stage => {
          const stageItems = items.filter(i => i.stage === stage.name);
          const isOver = dragOver === stage.name;

          return (
            <div key={stage.id}
              onDragOver={e => { e.preventDefault(); setDragOver(stage.name); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault();
                if (dragging) store.updatePipelineItem(dragging, { stage: stage.name });
                setDragging(null); setDragOver(null);
              }}
              style={{
                minWidth: 270, flex: 1, display: "flex", flexDirection: "column",
                borderRadius: 8,
                background: isOver ? stage.color + "06" : "transparent",
                border: isOver ? "2px dashed " + stage.color + "40" : "2px solid transparent",
                transition: "all 0.2s",
              }}>
              {/* Header */}
              <div style={{ padding: "10px 12px", marginBottom: 6, borderRadius: 5, background: stage.color + "08", borderLeft: "3px solid " + stage.color }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{stage.name}</span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 400, padding: "2px 6px", borderRadius: 3, background: stage.color + "15", color: stage.color }}>{stageItems.length}</span>
                    <button onClick={() => { setShowAdd(showAdd === stage.name ? null : stage.name); setNewItemData({}); }} style={{
                      background: "none", border: "1px solid " + T.bdr, borderRadius: 3, color: T.blue, fontSize: 12, cursor: "pointer", padding: "0 5px", lineHeight: "18px",
                    }}>+</button>
                  </div>
                </div>
              </div>

              {/* Add form - renders ALL fields */}
              {showAdd === stage.name && (
                <div style={{ margin: "0 4px 6px", padding: 10, background: T.card, border: "1px solid " + stage.color + "30", borderRadius: 8, borderLeft: "3px solid " + stage.color }}>
                  {fields.filter(f => f.type !== "calculation").map(f => (
                    <div key={f.id} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 9, color: T.tx3, marginBottom: 2 }}>{f.label}{f.required ? " *" : ""}</div>
                      <FieldInput field={f} value={newItemData[f.id]} onChange={v => setNewItemData(prev => ({ ...prev, [f.id]: v }))} compact />
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                    <button onClick={() => setShowAdd(null)} style={{ background: "none", border: "none", color: T.tx3, fontSize: 10, cursor: "pointer" }}>Cancel</button>
                    <Btn primary style={{ fontSize: 10 }} onClick={() => addItem(stage.name)}>Add</Btn>
                  </div>
                </div>
              )}

              {/* Cards */}
              <div style={{ flex: 1, overflow: "auto", padding: "0 4px" }}>
                {stageItems.map(item => {
                  const isSelected = selectedItem === item.id;
                  const isEditing = editingItem === item.id;
                  const titleVal = item[titleField?.id] || item.title || "Untitled";
                  const descVal = descField ? (item[descField.id] || "") : "";
                  const assigneeVal = assigneeField ? (item[assigneeField.id] || "Unassigned") : null;
                  const priVal = priField ? item[priField.id] : null;

                  return (
                    <div key={item.id}
                      draggable
                      onDragStart={() => setDragging(item.id)}
                      onDragEnd={() => { setDragging(null); setDragOver(null); }}
                      onClick={() => { setSelectedItem(isSelected ? null : item.id); setEditingItem(null); }}
                      style={{
                        padding: "10px 12px", marginBottom: 6, borderRadius: 8,
                        background: T.card, border: "1px solid " + (dragging === item.id ? stage.color : T.bdr),
                        cursor: "grab", transition: "all 0.15s",
                        opacity: dragging === item.id ? 0.5 : 1,
                        boxShadow: isSelected ? "0 0 0 2px " + stage.color + "40" : "none",
                      }}>
                      {/* Title + priority */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, flex: 1 }}>{titleVal}</div>
                        {priVal && <Badge color={priColors[priVal] || T.tx3}>{priVal}</Badge>}
                      </div>

                      {/* Description */}
                      {descVal && <div style={{ fontSize: 10, color: T.tx2, marginBottom: 6, lineHeight: 1.4 }}>{descVal}</div>}

                      {/* Visible field values on card (non-title, non-desc, non-textarea) */}
                      {cardFields.filter(f => f !== priField && f !== assigneeField && f.type !== "textarea" && item[f.id] !== undefined && item[f.id] !== "").length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                          {cardFields.filter(f => f !== priField && f !== assigneeField && f.type !== "textarea" && item[f.id] !== undefined && item[f.id] !== "").map(f => (
                            <span key={f.id} style={{
                              fontSize: 9, padding: "2px 6px", borderRadius: 3,
                              background: f.type === "calculation" ? T.pur + "10" : T.sf,
                              border: "1px solid " + (f.type === "calculation" ? T.pur + "20" : T.bdr),
                              color: f.type === "calculation" ? T.pur : T.tx2, fontWeight: 400,
                            }}>
                              {f.label}: {formatFieldValue(f, item[f.id], item)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Footer: assignee + date */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        {assigneeVal && (
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <div style={{ width: 18, height: 18, borderRadius: 9, background: T.blue + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 400, color: T.blue }}>
                              {assigneeVal[0]}
                            </div>
                            <span style={{ fontSize: 9, color: T.tx3 }}>{assigneeVal}</span>
                          </div>
                        )}
                        <span style={{ fontSize: 9, color: T.tx3 }}>{item.created}</span>
                      </div>

                      {/* Expanded: edit fields + stage + delete */}
                      {isSelected && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid " + T.bdr }} onClick={e => e.stopPropagation()}>
                          {/* Stage quick-switch */}
                          <div style={{ display: "flex", gap: 0, borderRadius: 4, border: "1px solid " + T.bdr, overflow: "hidden", marginBottom: 8 }}>
                            {stages.map(s => (
                              <button key={s.id} onClick={() => store.updatePipelineItem(item.id, { stage: s.name })} style={{
                                flex: 1, padding: "4px 2px", border: "none",
                                borderRight: s.id !== stages[stages.length-1]?.id ? "1px solid " + T.bdr : "none",
                                background: item.stage === s.name ? s.color : T.sf,
                                color: item.stage === s.name ? "#fff" : T.tx3,
                                fontSize: 7, fontWeight: 400, cursor: "pointer",
                              }}>{s.name}</button>
                            ))}
                          </div>

                          {/* Edit all fields */}
                          {isEditing ? (
                            <div>
                              {fields.filter(f => f.type !== "calculation").map(f => (
                                <div key={f.id} style={{ marginBottom: 6 }}>
                                  <div style={{ fontSize: 9, color: T.tx3, marginBottom: 2 }}>{f.label}</div>
                                  <FieldInput field={f} value={item[f.id]} onChange={v => updateItemField(item.id, f.id, v)} compact />
                                </div>
                              ))}
                              <button onClick={() => setEditingItem(null)} style={{
                                width: "100%", padding: "4px", borderRadius: 3, border: "1px solid " + T.blue,
                                background: T.blue + "10", color: T.blue, fontSize: 9, cursor: "pointer", marginTop: 4,
                              }}>Done Editing</button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => setEditingItem(item.id)} style={{
                                flex: 1, padding: "4px", borderRadius: 3, border: "1px solid " + T.blue,
                                background: "transparent", color: T.blue, fontSize: 9, cursor: "pointer",
                              }}>Edit Fields</button>
                              <button onClick={() => { store.removePipelineItem(item.id); setSelectedItem(null); }} style={{
                                flex: 1, padding: "4px", borderRadius: 3, border: "1px solid " + T.red + "30",
                                background: "transparent", color: T.red, fontSize: 9, cursor: "pointer",
                              }}>Delete</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
